import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import "./AdminForm.css";
import { API_BASE_URL, getSession } from "./apiConfig";

const ProductMaster = () => {
  const session = getSession();
  const userRole = session?.Role || "";

  const [activeTab, setActiveTab] = useState("entry");
  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);
  const emptyFormState = { partyId: "", productName: "", defaultRate: "" };
  const [form, setForm] = useState(emptyFormState);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(true);

  const loadParties = async () => {
    const res = await fetch(`${API_BASE_URL}/party`);
    setParties(await res.json());
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/product/all`);
      setProducts(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadParties(); loadProducts(); }, []);

  const resetForm = () => {
    setForm(emptyFormState);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.partyId) {
      setMessage({ type: "error", text: "Please select a party." });
      return;
    }
    if (!form.productName.trim()) {
      setMessage({ type: "error", text: "Product name is required." });
      return;
    }

    const isEditing = editingId !== null;
    const payload = {
      partyId: parseInt(form.partyId, 10),
      productName: form.productName,
      defaultRate: parseFloat(form.defaultRate) || 0,
    };

    const res = await fetch(
      isEditing ? `${API_BASE_URL}/product/${editingId}` : `${API_BASE_URL}/product`,
      {
        method: isEditing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    if (res.ok) {
      setMessage({ type: "success", text: isEditing ? "Product updated successfully." : "Product added successfully." });
      setForm({ ...emptyFormState, partyId: isEditing ? "" : form.partyId });
      setEditingId(null);
      loadProducts();
      setActiveTab("view");
    } else {
      setMessage({ type: "error", text: isEditing ? "Could not update product." : "Could not save product." });
    }
  };

  // `p` here comes from GET /api/product/all, which returns an explicit
  // camelCase projection (productId, partyId, partyName, productName, defaultRate).
  const handleEdit = (p) => {
    setForm({
      partyId: p.partyId,
      productName: p.productName || "",
      defaultRate: p.defaultRate ?? "",
    });
    setEditingId(p.productId);
    setMessage({ type: "", text: "" });
    setActiveTab("entry");
  };

  const handleDelete = async (p) => {
    if (userRole !== "Admin") {
      setMessage({ type: "error", text: "Only Admin users can delete records." });
      return;
    }
    if (!window.confirm(`Delete product "${p.productName}"?`)) return;

    try {
      const res = await fetch(`${API_BASE_URL}/product/${p.productId}`, {
        method: "DELETE",
        headers: { "X-User-Role": userRole },
      });
      if (!res.ok) {
        // 409 = blocked by the backend because this product is used in
        // one or more transactions
        const text = await res.text().catch(() => "");
        throw new Error(text || "Delete failed");
      }
      setMessage({ type: "success", text: "Product deleted successfully." });
      loadProducts();
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Error deleting product." });
    }
  };

  return (
    <AdminLayout>
      <div className="page-header">
        <h1 className="page-title">Product Master</h1>
      </div>

      <div className="content-card">
        <div className="tab-container">
          <button
            className={`tab-btn ${activeTab === "entry" ? "active" : ""}`}
            onClick={() => setActiveTab("entry")}
          >
            {editingId !== null ? "Edit Product" : "Create New Product"}
          </button>
          <button
            className={`tab-btn ${activeTab === "view" ? "active" : ""}`}
            onClick={() => setActiveTab("view")}
          >
            View Records ({products.length})
          </button>
        </div>

        {activeTab === "entry" && (
          <div className="form-wrapper">
            {message.text && (
              <div className={`alert ${message.type === "success" ? "alert-success" : "alert-error"}`}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Party / Company *</label>
                  <select
                    className="form-control"
                    value={form.partyId}
                    onChange={(e) => setForm({ ...form, partyId: e.target.value })}
                    required
                  >
                    <option value="">-- Select Party --</option>
                    {parties.map((p) => (
                      <option key={p.partyId} value={p.partyId}>{p.partyName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Product Name *</label>
                  <input
                    className="form-control"
                    value={form.productName}
                    onChange={(e) => setForm({ ...form, productName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Default Rate</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.defaultRate}
                    onChange={(e) => setForm({ ...form, defaultRate: e.target.value })}
                  />
                </div>
              </div>

              <button type="submit" className="btn-submit">
                {editingId !== null ? "Update Product" : "Add Product"}
              </button>
              {editingId !== null && (
                <button type="button" className="btn-secondary-sm" style={{ marginLeft: "10px" }} onClick={resetForm}>
                  Cancel Edit
                </button>
              )}
            </form>
          </div>
        )}

        {activeTab === "view" && (
          <div className="table-wrap">
            {loading ? (
              <div className="empty-state">Loading records...</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Party</th>
                    <th>Product</th>
                    <th>Default Rate</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.length > 0 ? (
                    products.map((p) => (
                      <tr key={p.productId}>
                        <td><strong>{p.partyName}</strong></td>
                        <td>{p.productName}</td>
                        <td>{p.defaultRate}</td>
                        <td>
                          <button className="btn-secondary-sm" onClick={() => handleEdit(p)}>Edit</button>
                          <button
                            className="btn-secondary-sm"
                            style={{ marginLeft: "6px", color: "#dc2626", borderColor: "#fecaca" }}
                            onClick={() => handleDelete(p)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="empty-state">
                        <div style={{ marginBottom: "8px", fontSize: "1.2rem" }}>📦</div>
                        No products found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ProductMaster;